// API wrappers for the Community "Connectors" tab — browser-automation of social
// platforms (LinkedIn + Facebook) for operations the official platform APIs do
// NOT expose (invite people, in-app message). Backend mounted at /api/connectors.
//
// A server-side headless browser drives a real login session: the FE streams the
// browser's screenshot and forwards click/keyboard input, so the operator logs in
// (including 2FA) without us ever storing their password — we capture the session
// only. Every invite/message is tracked as an "activity" and every targeted person
// is rolled up into a "contact" directory.
//
// Thin pass-throughs to the shared fetch helpers in ../index (Bearer auth header
// is set globally there via setJWT, same as every other Community page).
import { url, post, get, remove, globalPut } from "../index";

export { url }; // re-export so pages can build static asset URLs (e.g. the extension .zip)

const BASE = "/api/connectors";

function qs(params = {}) {
  const p = Object.entries(params).filter(([, v]) => v != null && v !== "");
  return p.length ? "?" + p.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&") : "";
}

// ---- Sessions (per platform) ---------------------------------------------

// [{_id, platform, label, status:'active'|'needs_login'|'expired', lastUsedAt, updatedAt}]
export const getSessions = (params) => get(`${BASE}/sessions${qs(params)}`);

// Drop a stored session (DELETE).
export const deleteSession = (id) => remove(`${BASE}/sessions/${id}`);

// ---- Streamed driven-login -----------------------------------------------

// Spin up a headless browser session → {sessionId, viewport:{width,height}}.
export const startConnect = (data) => post(`${BASE}/connect/start`, data, false);

// Absolute URL for the live screenshot <img> (image/jpeg). Poll with a
// cache-buster (?t=…). The bundle is served by the same backend host that
// answers /api/*, so this resolves to the right origin.
export const screenshotUrl = (id, vt, t) => `${url}${BASE}/connect/${id}/screenshot?vt=${encodeURIComponent(vt || "")}&t=${t}`;

// Forward a single input event to the driven browser:
//   {type:'click'|'text'|'key'|'scroll', x, y, text, key, deltaY}
export const sendInput = (id, body) => post(`${BASE}/connect/${id}/input`, body, false);

// "I've logged in — Capture": persist the authenticated session.
//   → {ok:true,status,label} | {ok:false,reason:'not_logged_in'}
export const captureConnect = (id, data) => post(`${BASE}/connect/${id}/capture`, data, false);

// Tear the driven browser down without capturing.
export const cancelConnect = (id) => post(`${BASE}/connect/${id}/cancel`, {}, false);

// ---- Cookie import -------------------------------------------------------

// Import a session from cookies the operator exported (Cookie-Editor → JSON) in
// their own browser — the datacenter-IP login is often blocked, so this is the
// fallback. {brandId, platform, cookies} (cookies as a JSON string or array).
//   → {ok:true, status:'active', count} | {error:'...'}
export const importConnect = (data) => post(`${BASE}/import`, data, false);

// ---- Operations + tracking -----------------------------------------------

// Queue an op: {brandId, platform, op:'message'|'invite', target:{url,name}, payload:{text,note}}
//   → {ok, activityId}
export const runOp = (data) => post(`${BASE}/op`, data, false);

// Activity log, filtered by type=invites|messages|all and optional subType (invite|invite_to_page|search_invite|message|follow_page).
export const getActivities = (params) => get(`${BASE}/activities${qs(params)}`);

// Retry a single failed activity → {ok, activityId, status:'queued'}
export const retryActivity = (id) => post(`${BASE}/activities/${id}/retry`, {}, false);

// Retry ALL failed activities for a brand (optionally filtered by op) → {ok, retried}
export const retryAllActivities = (data) => post(`${BASE}/activities/retry-all`, data, false);

// Collected/tracked contact directory.
//   [{url,name,platform,count,lastOp:{op,status,createdAt}}]
export const getConnectorContacts = (params) => get(`${BASE}/contacts${qs(params)}`);

// Bulk-cancel queued/running activities for a brand (by op or activityIds).
export const bulkCancelActivities = (data) => post(`${BASE}/activities/bulk-cancel`, data, false);

// Pause or resume a connector session.
export const toggleSessionPause = (id, pause) => fetch(`${url}${BASE}/sessions/${id}/pause`, {
  method: 'PATCH',
  headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + (localStorage.getItem('token') || '') },
  body: JSON.stringify({ pause }),
});

// Activity timeline for a specific contact profile URL.
export const getContactTimeline = (params) => get(`${BASE}/contact-timeline${qs(params)}`);

// ---- In-browser agent (Prigmar Connector extension) ----------------------
// The browser EXTENSION runs invite/message ops inside the operator's own
// browser (their IP, their logged-in session) — no datacenter IP, no cookie
// export. Each brand gets a token the extension authenticates with.

// Get-or-create the brand's agent token.  {brandId} → {token:"<hex>"}
export const getAgentToken = (params) => get(`${BASE}/agent/token${qs(params)}`);

// Liveness of the company's extension.  → {hasToken, online, lastSeenAt}
export const getAgentStatus = (params) => get(`${BASE}/agent-status${qs(params)}`);

// Company connector controls (unified session checkboxes).
//   GET → { catalog, allowed, checked, brandsChecked }
export const getConnectorPrefs = () => get(`${BASE}/prefs`);
//   PUT { platformsChecked?:{plat:bool}, brandsChecked?:[id]|null } → same shape
export const saveConnectorPrefs = (data) => globalPut(`${BASE}/prefs`, data, false);

// ---- Audience: harvest → categorize → targeted outreach ------------------
// Queue a harvest op (the extension scrapes your connections/friends).  {brandId, platform}
export const harvestContacts = (data) => post(`${BASE}/contacts/harvest`, data, false);
export const discoverContacts = (data) => post(`${BASE}/contacts/discover`, data, false);
// Score every contact's relevance to this brand (worker LLM).  {brandId}
export const categorizeContacts = (data) => post(`${BASE}/contacts/categorize`, data, false);
// Generate personalized invites/messages for the top contacts.  {brandId, op:'invite'|'message', limit}
export const generateOutreach = (data) => post(`${BASE}/contacts/generate-outreach`, data, false);
// Synchronous single-contact draft for the quick popup's "Generate" button.
export const draftMessage = (data) => post(`${BASE}/contacts/draft-message`, data, false);
// Harvested contacts with this brand's relevance tier.  {brandId} → {data:[{name,headline,tier,score,reason,contacted,...}]}
export const getScoredContacts = (params) => get(`${BASE}/contacts-scored${qs(params)}`);

// ---- Stats + event log dashboard ------------------------------------------
// Aggregated counters for the permanent stats bar: activity tallies, harvest
// totals, outreach counts, extension health, success rate.
export const getConnectorStats = (params) => get(`${BASE}/stats${qs(params)}`);

// Drill-down: list activities for a specific stat (status + optional op filter).
export const getStatsDetail = (params) => get(`${BASE}/stats/detail${qs(params)}`);

// Paginated event log for the activity log panel.  {brandId, type, activityId, limit, before}
// Returns {data:[ConnectorEvent], hasMore, cursor} for infinite scroll.
export const getConnectorEvents = (params) => get(`${BASE}/events${qs(params)}`);

// ---- Auto-invite config + caps --------------------------------------------
// Get auto-invite config + platform limit reference.  {brandId}
export const getAutoInviteConfig = (params) => get(`${BASE}/auto-invite/config${qs(params)}`);
// Save auto-invite config.  {brandId, enabled, platforms:{linkedin:{enabled,dailyCap,...}, facebook:{...}}}
export const saveAutoInviteConfig = (data) => globalPut(`${BASE}/auto-invite/config`, data, false);
// Company-wide auto-invite view: master switch + every brand's config in one call.
//   → { autoInviteEnabled, configs:[{brand_id, enabled, platforms, pacing}], platformLimits }
export const getAutoInviteAll = () => get(`${BASE}/auto-invite/all`);
// Current counts vs caps.  {brandId}
export const getAutoInviteStatus = (params) => get(`${BASE}/auto-invite/status${qs(params)}`);

// ---- Conversations — full thread tracking + AI continuation ----------------
export const getConversations = (params) => get(`${BASE}/conversations${qs(params)}`);
export const getConversation = (id) => get(`${BASE}/conversations/${id}`);
export const sendConversationMessage = (id, data) => post(`${BASE}/conversations/${id}/messages`, data, false);
export const createConversation = (data) => post(`${BASE}/conversations`, data, false);
export const sendConversationMsg = (id, text) => post(`${BASE}/conversations/${id}/messages`, { text }, false);
export const generateAiReply = (id) => post(`${BASE}/conversations/${id}/ai-reply`, {}, false);
export const updateConversation = (id, data) => globalPut(`${BASE}/conversations/${id}`, data, false);
// DM funnel: per-conversation goal override is updateConversation({conversionGoal}).
// This sets the BRAND DEFAULT goal (new conversations inherit it).
export const setBrandGoal = (data) => post(`${BASE}/brand-goal`, data, false);

// ── Skool ───────────────────────────────────────────────────────────────────
// Skool has no official API, so the backend speaks its internal JSON API using
// the operator's harvested session (connector extension v0.40.0+ syncs the whole
// skool.com cookie jar). These configure the bound community and its automation:
// the content mirror (posts/articles/FAQs), the weekly poll, and paced DM
// outreach into other SaaS communities.
export const getSkoolAccounts = () => get(`${BASE}/skool/accounts`, false);
export const saveSkoolAccount = (data) => post(`${BASE}/skool/account`, data, false);
export const queueSkoolOp = (data) => post(`${BASE}/skool/op`, data, false);

// ---- Skool prospect states (worker services/prospectClassifier) -----------
// Where each Skool person stands — new / engaged / close_to_converting /
// booked / converted / not_interested — and the operator override, which beats
// every automatic classification until set back to 'auto'.
export const getSkoolProspects = (params) => get(`${BASE}/skool/prospects${qs(params)}`);
export const setSkoolProspectState = (id, state, reason = "") =>
  post(`${BASE}/skool/prospects/${id}/state`, { state, reason });
// Skool mentions (the mention lane's replies) with the mentioner's prospect state.
export const getSkoolMentions = (params) => get(`${BASE}/skool/mentions${qs(params)}`);
// Skool welcome comments with the welcomed member's prospect state.
export const getSkoolWelcomes = (params) => get(`${BASE}/skool/welcomes${qs(params)}`);
// Skool onboarding ladder per new member, with prospect state + whether it is stopped.
export const getSkoolOnboarding = (params) => get(`${BASE}/skool/onboarding${qs(params)}`);
// Skool nudges per member, with prospect state + whether another may come.
export const getSkoolNudges = (params) => get(`${BASE}/skool/nudges${qs(params)}`);
